import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "src/components/ui/alert";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";
import { sanitizeBrlValue } from "../simulador-poupanca-selic";
import { RetirementResponse } from "./components/RetirementResponse";

interface RetirementResults {
  monthlyInvestment: number;
  totalSaved: number;
  monthlyRetirementIncome: number;
  inheritanceLeft: number;
  monthsToRetirement: number;
  retirementGoal: number;
  annualReturnRate: number;
  monthlyRetirementExpense: number;
  monthlyIncome: number;
  maxMonthlySpending: number;
}

const calculatorKey = "simulador-aposentadoria";

export const SimuladorAposentadoria = () => {
  const [monthlyIncome, setMonthlyIncome] = useState<string>("");
  const [currentInvestments, setCurrentInvestments] = useState<string>("");
  const [retirementGoal, setRetirementGoal] = useState<string>("");
  const [investmentPercentage, setInvestmentPercentage] = useState<string>("");
  const [currentAge, setCurrentAge] = useState<string>("");
  const [retirementAge, setRetirementAge] = useState<string>("");
  const [annualReturn, setAnnualReturn] = useState<string>("");
  const [monthlyRetirementExpense, setMonthlyRetirementExpense] =
    useState<string>("");
  const [error, setError] = useState<string>("");
  const [results, setResults] = useState<RetirementResults | null>(null);
  function clear() {
    setMonthlyIncome("");
    setCurrentInvestments("");
    setRetirementGoal("");
    setInvestmentPercentage("");
    setCurrentAge("");
    setRetirementAge("");
    setAnnualReturn("");
    setMonthlyRetirementExpense("");
    setResults(null);
    setError("");
  }

  const calculateRetirement = () => {
    setError("");

    if (
      !monthlyIncome ||
      !currentInvestments ||
      !retirementGoal ||
      !investmentPercentage ||
      !currentAge ||
      !retirementAge ||
      !annualReturn ||
      !monthlyRetirementExpense
    ) {
      setError("Por favor, preencha todos os campos");
      return;
    }

    const income = Number.parseFloat(
      monthlyIncome.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );
    const investments = Number.parseFloat(
      currentInvestments.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );
    const goal = Number.parseFloat(
      retirementGoal.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );
    const percentage = Number.parseFloat(investmentPercentage) / 100;
    const age = Number.parseInt(currentAge);
    const retireAge = Number.parseInt(retirementAge);
    const annualReturnRate = Number.parseFloat(annualReturn) / 100;
    const retirementExpense = Number.parseFloat(
      monthlyRetirementExpense.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );

    if (
      income <= 0 ||
      investments < 0 ||
      goal <= 0 ||
      percentage <= 0 ||
      age <= 0 ||
      retireAge <= age ||
      annualReturnRate <= 0 ||
      retirementExpense <= 0
    ) {
      setError("Por favor, verifique os valores inseridos");
      return;
    }

    const monthsToRetirement = (retireAge - age) * 12;
    const monthlyInvestment = income * percentage;
    const monthlyRate = Math.pow(1 + annualReturnRate, 1 / 12) - 1;

    let totalSaved = investments;
    for (let i = 0; i < monthsToRetirement; i++) {
      totalSaved = totalSaved * (1 + monthlyRate) + monthlyInvestment;
    }

    const monthlyRetirementIncome = totalSaved * monthlyRate;

    const monthsAfterRetirement = Math.max(0, (72 - retireAge) * 12);
    let inheritanceLeft = totalSaved;
    for (let i = 0; i < monthsAfterRetirement; i++) {
      inheritanceLeft = inheritanceLeft * (1 + monthlyRate) - retirementExpense;
      if (inheritanceLeft < 0) {
        inheritanceLeft = 0;
        break;
      }
    }

    let maxMonthlySpending = monthlyRetirementIncome;
    if (inheritanceLeft <= 0) {
      let low = 0;
      let high = monthlyRetirementIncome;
      while (high - low > 1) {
        const mid = (low + high) / 2;
        let testInheritance = totalSaved;
        for (let i = 0; i < monthsAfterRetirement; i++) {
          testInheritance = testInheritance * (1 + monthlyRate) - mid;
          if (testInheritance < 0) break;
        }
        if (testInheritance > 0) {
          low = mid;
        } else {
          high = mid;
        }
      }
      maxMonthlySpending = low;
    }

    setResults({
      monthlyInvestment: Number.parseFloat(monthlyInvestment.toFixed(2)),
      totalSaved: Number.parseFloat(totalSaved.toFixed(2)),
      monthlyRetirementIncome: Number.parseFloat(
        monthlyRetirementIncome.toFixed(2)
      ),
      inheritanceLeft: Number.parseFloat(inheritanceLeft.toFixed(2)),
      monthsToRetirement,
      retirementGoal: goal,
      annualReturnRate: annualReturnRate,
      monthlyRetirementExpense: retirementExpense,
      monthlyIncome: income,
      maxMonthlySpending: Number.parseFloat(maxMonthlySpending.toFixed(2)),
    });
  };

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      monthlyIncome,
      currentInvestments,
      retirementGoal,
      investmentPercentage,
      currentAge,
      retirementAge,
      annualReturn,
      monthlyRetirementExpense,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setMonthlyIncome((savedData.monthlyIncome as string) || "");
    setCurrentInvestments((savedData.currentInvestments as string) || "");
    setRetirementGoal((savedData.retirementGoal as string) || "");
    setInvestmentPercentage((savedData.investmentPercentage as string) || "");
    setCurrentAge((savedData.currentAge as string) || "");
    setRetirementAge((savedData.retirementAge as string) || "");
    setAnnualReturn((savedData.annualReturn as string) || "");
    setMonthlyRetirementExpense(
      (savedData.monthlyRetirementExpense as string) || ""
    );
  }, []);

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-primary text-xl font-bold">
            Simulador de Aposentadoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
              checkCalculatorUsageLimitAndRedirect(calculateRetirement);
            }}
            className="grid gap-4"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="monthlyIncome">
                  Quanto você ganha por mês?
                </Label>
                <Input
                  id="monthlyIncome"
                  type="text"
                  placeholder="5.000,00"
                  value={monthlyIncome}
                  onChange={(e) => setMonthlyIncome(sanitizeBrlValue(e))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="currentInvestments">
                  Quanto você já tem investido?
                </Label>
                <Input
                  id="currentInvestments"
                  type="text"
                  placeholder="50.000,00"
                  value={currentInvestments}
                  onChange={(e) => setCurrentInvestments(sanitizeBrlValue(e))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="retirementGoal">
                  Com quanto de patrimônio você quer se aposentar?
                </Label>
                <Input
                  id="retirementGoal"
                  type="text"
                  placeholder="1.000.000,00"
                  value={retirementGoal}
                  onChange={(e) => setRetirementGoal(sanitizeBrlValue(e))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="investmentPercentage">
                  Quantos % da sua renda que você investe?
                </Label>
                <Input
                  id="investmentPercentage"
                  type="number"
                  placeholder="20"
                  value={investmentPercentage}
                  onChange={(e) => setInvestmentPercentage(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="currentAge">Qual sua idade atual</Label>
                <Input
                  id="currentAge"
                  type="number"
                  placeholder="30"
                  value={currentAge}
                  onChange={(e) => setCurrentAge(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="retirementAge">
                  Com quantos anos você deseja se aposentar?
                </Label>
                <Input
                  id="retirementAge"
                  type="number"
                  placeholder="65"
                  value={retirementAge}
                  onChange={(e) => setRetirementAge(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="annualReturn">
                  Sua rentabilidade total anual projetada
                </Label>
                <Input
                  id="annualReturn"
                  type="number"
                  placeholder="10"
                  value={annualReturn}
                  onChange={(e) => setAnnualReturn(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="monthlyRetirementExpense">
                  Quanto você pretende gastar por mês aposentado?
                </Label>
                <Input
                  id="monthlyRetirementExpense"
                  type="text"
                  placeholder="10.000,00"
                  value={monthlyRetirementExpense}
                  onChange={(e) =>
                    setMonthlyRetirementExpense(sanitizeBrlValue(e))
                  }
                />
              </div>
            </div>

            <div className="flex justify-between">
              <Button
                type="submit"
                variant={"default"}
                className="sm:w-[150px]"
              >
                Calcular
              </Button>
              <Button onClick={clear} variant={"ghost"}>
                Limpar
              </Button>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </form>

          {results && <RetirementResponse results={results} />}
        </CardContent>
      </Card>
    </div>
  );
};
